import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Cesium from 'cesium';
import { Send, Smile, AtSign, Paperclip } from 'lucide-react';
import { Avatar } from '../Avatar';
import { TagInput } from './TagInput';
import { MentionPicker } from './MentionPicker';
import { AttachmentUploader } from './AttachmentUploader';
// Geometry — single unified body holds two rows (composer + tags+tools)
// stacked vertically with no internal break, no divider, no gap. One
// charcoal surface, one rounded-rectangle shape. The radius is generous
// (20) for a soft squircle feel without being a perfect pill — at this
// height a true pill (radius 38) would clip the avatar against its left
// curve.
const BODY_W = 340;
const COMPOSER_ROW_H = 40;
const TAG_ROW_H = 36;
// 4px gap between composer row and tag row, 4px gap below tag row to the
// body bottom — so the TagInput pill (centred inside the 36-tall tag row)
// ends up ~6px from the body's bottom edge, mirroring the avatar's ~6px
// from the body's top edge.
const COMPOSER_TO_TAG_GAP = 4;
const TAG_TO_BOTTOM_GAP = 4;
const BODY_H = COMPOSER_ROW_H + COMPOSER_TO_TAG_GAP + TAG_ROW_H + TAG_TO_BOTTOM_GAP; // 84
const BODY_RADIUS = 20;
const AVATAR_SIZE = 28;
// Horizontal inset matches (and slightly exceeds) the send button's right
// inset so the avatar has breathing room from the pill's left edge — was
// 2px before, which made the avatar hug the border while the send button
// sat with 6px of buffer. 8px also matches the avatar's vertical buffer
// (40 row height − 28 avatar) / 2 = 6, with a touch extra horizontally.
const AVATAR_INSET_X = 8;
const AVATAR_TOP = (COMPOSER_ROW_H - AVATAR_SIZE) / 2; // 6
const SEND_SIZE = 28;
const SEND_INSET_RIGHT = 6;
const SEND_X = BODY_W - SEND_SIZE - SEND_INSET_RIGHT; // 306
const SEND_Y = (COMPOSER_ROW_H - SEND_SIZE) / 2; // 6
const INPUT_LEFT = AVATAR_INSET_X + AVATAR_SIZE + 8; // 38
const INPUT_RIGHT_GUTTER = SEND_SIZE + SEND_INSET_RIGHT + 8; // 42
// Nub at the avatar's bottom-centre. Drawn at fixed container coords; it
// renders the same charcoal as the body, so when absorbed inside the body
// it's invisible — the world anchor is then just a point on the body's
// inner surface (where the avatar's bottom is).
const NUB_W = 8;
const NUB_H = 4;
// Centre nub horizontally under the avatar (avatar centre x = inset + size/2).
const NUB_X = AVATAR_INSET_X + AVATAR_SIZE / 2 - NUB_W / 2; // 18
const NUB_Y = 32;
const NUB_TIP_X = NUB_X + NUB_W / 2; // 22
const NUB_TIP_Y = NUB_Y + NUB_H; // 36
// Tag-row content layout (within the unified body, below the composer row).
// The tag row's left padding matches the avatar's inset so the TagInput's
// pill aligns with the avatar's left edge.
const TAG_ROW_TOP = COMPOSER_ROW_H + COMPOSER_TO_TAG_GAP; // 44
const TAG_ROW_PAD_X = AVATAR_INSET_X; // 8
const TOOLS_BUTTON_SIZE = 24;
const TOOLS_BUTTON_GAP = 2;
const TOOLS_INSET_RIGHT = 8;
// Container width = body width.
const CONTAINER_W = BODY_W;
// Shadow — lifted to indicate the draft is "live" (waiting on you).
const PILL_SHADOW = '0 6px 18px rgba(0, 0, 0, 0.5)';
const SHELL = 'var(--charcoal)';
const SAND = 'var(--sand)';
const DEW = 'var(--dew)';
// Same curated emoji palette as CompactCommentComposer / older draft pill.
const EMOJI_PALETTE = [
    '😀', '😄', '😅', '😂', '🙂', '😉', '😊', '😍',
    '🤔', '😎', '😴', '😬', '🤯', '😭', '😡', '🥲',
    '👍', '👎', '👌', '🙌', '👏', '🙏', '💪', '👀',
    '🔥', '⭐', '✨', '💡', '✅', '❌', '⚠️', '❗',
    '📍', '🗺️', '🏞️', '🌲', '🌊', '🏔️', '🚧', '🛠️',
    '📝', '📷', '🔍', '⏱️', '🚀', '🎯', '💬', '🤝',
];
export const CommentDraftInline = ({ viewer, draftAnchor, preLinkedArtifactId, currentUser, collaborators, tagSuggestions, onClose, onSubmit, }) => {
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const mentionStartRef = useRef(null);
    // Composer state
    const [content, setContent] = useState('');
    const [tags, setTags] = useState([]);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [mentionedUserIds, setMentionedUserIds] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionAnchor, setMentionAnchor] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Per-frame screen-position tracking. Same pattern as CommentHtmlPin.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const world = Cesium.Cartesian3.fromDegrees(draftAnchor.longitude, draftAnchor.latitude, draftAnchor.height);
        const update = () => {
            if (!containerRef.current || viewer.isDestroyed())
                return;
            const win = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, world);
            if (!win) {
                containerRef.current.style.display = 'none';
                return;
            }
            containerRef.current.style.display = '';
            containerRef.current.style.transform =
                `translate3d(${win.x - NUB_TIP_X}px, ${win.y - NUB_TIP_Y}px, 0)`;
        };
        update();
        return viewer.scene.postRender.addEventListener(update);
    }, [viewer, draftAnchor.latitude, draftAnchor.longitude, draftAnchor.height]);
    // Esc cancels.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    // Autofocus the input on mount.
    useEffect(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    // --- Submit ---
    const canSubmit = content.trim().length > 0 && !isSubmitting;
    const handleSubmit = async () => {
        if (!canSubmit)
            return;
        setIsSubmitting(true);
        try {
            const validAttachments = pendingAttachments.filter(a => !a.error).map(a => a.file);
            const artifactIds = preLinkedArtifactId ? [preLinkedArtifactId] : [];
            await onSubmit({
                content: content.trim(),
                attachments: validAttachments,
                artifactIds,
                mentionedUserIds,
                anchor: draftAnchor,
                tags,
            });
            pendingAttachments.forEach(a => { if (a.previewUrl)
                URL.revokeObjectURL(a.previewUrl); });
            onClose();
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (canSubmit)
                handleSubmit();
        }
    };
    // --- Mention picker ---
    const detectMentionTrigger = useCallback((value, cursorPos) => {
        if (collaborators.length === 0) {
            setMentionQuery(null);
            mentionStartRef.current = null;
            return;
        }
        let i = cursorPos - 1;
        while (i >= 0) {
            const ch = value[i];
            if (ch === '@')
                break;
            if (ch === ' ' || ch === '\n' || ch === '\t') {
                setMentionQuery(null);
                mentionStartRef.current = null;
                return;
            }
            i--;
        }
        if (i < 0) {
            setMentionQuery(null);
            mentionStartRef.current = null;
            return;
        }
        const prev = i > 0 ? value[i - 1] : '';
        if (prev && prev !== ' ' && prev !== '\n' && prev !== '\t') {
            setMentionQuery(null);
            mentionStartRef.current = null;
            return;
        }
        mentionStartRef.current = i;
        setMentionQuery(value.slice(i + 1, cursorPos));
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setMentionAnchor({ x: rect.left, y: rect.bottom + 4 });
        }
    }, [collaborators.length]);
    const handleContentChange = (e) => {
        var _a;
        const value = e.target.value;
        setContent(value);
        detectMentionTrigger(value, (_a = e.target.selectionStart) !== null && _a !== void 0 ? _a : value.length);
    };
    const handlePickMention = (user) => {
        var _a, _b;
        if (mentionStartRef.current === null)
            return;
        const start = mentionStartRef.current;
        const cursorPos = (_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart) !== null && _b !== void 0 ? _b : content.length;
        const displayName = (user.firstName || user.lastName)
            ? `${user.firstName} ${user.lastName}`.trim()
            : user.email;
        const before = content.slice(0, start);
        const after = content.slice(cursorPos);
        const next = `${before}@${displayName} ${after}`;
        setContent(next);
        if (!mentionedUserIds.includes(user.userId)) {
            setMentionedUserIds(prev => [...prev, user.userId]);
        }
        setMentionQuery(null);
        mentionStartRef.current = null;
        setTimeout(() => {
            if (inputRef.current) {
                const caret = before.length + displayName.length + 2;
                inputRef.current.focus();
                inputRef.current.setSelectionRange(caret, caret);
            }
        }, 0);
    };
    const insertAtCursor = (toInsert) => {
        var _a, _b;
        const ta = inputRef.current;
        if (!ta)
            return;
        const start = (_a = ta.selectionStart) !== null && _a !== void 0 ? _a : content.length;
        const end = (_b = ta.selectionEnd) !== null && _b !== void 0 ? _b : content.length;
        const next = content.slice(0, start) + toInsert + content.slice(end);
        setContent(next);
        setTimeout(() => {
            ta.focus();
            const caret = start + toInsert.length;
            ta.setSelectionRange(caret, caret);
            detectMentionTrigger(next, caret);
        }, 0);
    };
    // --- Emoji picker ---
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiBtnRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const [emojiPickerPos, setEmojiPickerPos] = useState(null);
    useEffect(() => {
        if (!showEmojiPicker)
            return;
        const onClickOutside = (e) => {
            var _a, _b;
            const target = e.target;
            if ((_a = emojiPickerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            if ((_b = emojiBtnRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))
                return;
            setShowEmojiPicker(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [showEmojiPicker]);
    const onEmojiBtnClick = () => {
        if (!showEmojiPicker && emojiBtnRef.current) {
            const rect = emojiBtnRef.current.getBoundingClientRect();
            setEmojiPickerPos({ x: rect.left, y: rect.top });
        }
        setShowEmojiPicker(s => !s);
    };
    const pickEmoji = (emoji) => {
        insertAtCursor(emoji);
        setShowEmojiPicker(false);
    };
    // --- Attach ---
    const fileInputRef = useRef(null);
    const onAttachClick = () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); };
    const onFilesPicked = (e) => {
        var _a;
        const files = Array.from((_a = e.target.files) !== null && _a !== void 0 ? _a : []);
        if (files.length === 0)
            return;
        const newAttachments = files.map(file => ({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            file,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
            status: 'pending',
            progress: 0,
        }));
        setPendingAttachments(prev => [...prev, ...newAttachments]);
        if (fileInputRef.current)
            fileInputRef.current.value = '';
    };
    // --- Render ---
    const hasAttachments = pendingAttachments.length > 0;
    // Stop pointer events from bubbling out to the map (clicks inside the
    // composer shouldn't drop another comment pin behind us).
    const stop = (e) => e.stopPropagation();
    return (_jsxs("div", { ref: containerRef, style: {
            position: 'fixed',
            left: 0,
            top: 0,
            width: CONTAINER_W,
            willChange: 'transform',
            userSelect: 'none',
            zIndex: 1200, // above hover pills (1100) and below thread popovers (1200/1199 leader)
        }, onMouseDown: stop, onClick: stop, children: [_jsx("div", { style: {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: BODY_W,
                    height: BODY_H,
                    backgroundColor: SHELL,
                    borderRadius: BODY_RADIUS,
                    boxShadow: PILL_SHADOW,
                } }), _jsx("div", { style: {
                    position: 'absolute',
                    left: NUB_X,
                    top: NUB_Y,
                    width: 0,
                    height: 0,
                    borderLeft: `${NUB_W / 2}px solid transparent`,
                    borderRight: `${NUB_W / 2}px solid transparent`,
                    borderTop: `${NUB_H}px solid ${SHELL}`,
                } }), _jsx(Avatar, { userId: currentUser.userId, commentColor: currentUser.commentColor, photoUrl: currentUser.avatarUrl, source: currentUser, size: AVATAR_SIZE, style: {
                    position: 'absolute',
                    left: AVATAR_INSET_X,
                    top: AVATAR_TOP,
                    fontSize: 13,
                    fontWeight: 'bold',
                } }), _jsx("input", { ref: inputRef, type: "text", value: content, onChange: handleContentChange, onKeyUp: (e) => { var _a; return detectMentionTrigger(e.target.value, (_a = e.target.selectionStart) !== null && _a !== void 0 ? _a : 0); }, onClick: (e) => { var _a; return detectMentionTrigger(e.currentTarget.value, (_a = e.currentTarget.selectionStart) !== null && _a !== void 0 ? _a : 0); }, onKeyDown: handleKeyDown, placeholder: "Add a comment...", disabled: isSubmitting, style: {
                    position: 'absolute',
                    left: INPUT_LEFT,
                    top: 0,
                    width: BODY_W - INPUT_LEFT - INPUT_RIGHT_GUTTER,
                    height: COMPOSER_ROW_H,
                    backgroundColor: 'transparent',
                    color: SAND,
                    fontSize: 13,
                    border: 'none',
                    outline: 'none',
                    padding: 0,
                } }), _jsx("button", { type: "button", onClick: handleSubmit, disabled: !canSubmit, title: canSubmit ? 'Post (⏎)' : 'Type a comment to post', style: {
                    position: 'absolute',
                    left: SEND_X,
                    top: SEND_Y,
                    width: SEND_SIZE,
                    height: SEND_SIZE,
                    borderRadius: '50%',
                    backgroundColor: canSubmit ? 'var(--fern)' : 'rgba(255, 255, 255, 0.08)',
                    color: canSubmit ? 'white' : DEW,
                    border: 'none',
                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 120ms ease',
                }, children: _jsx(Send, { size: 14 }) }), _jsxs("div", { style: {
                    position: 'absolute',
                    left: 0,
                    top: TAG_ROW_TOP,
                    width: BODY_W,
                    height: TAG_ROW_H,
                    padding: `0 ${TOOLS_INSET_RIGHT}px 0 ${TAG_ROW_PAD_X}px`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxSizing: 'border-box',
                }, children: [_jsx("div", { style: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }, children: _jsx(TagInput, { value: tags, onChange: setTags, suggestions: tagSuggestions, disabled: isSubmitting }) }), _jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: TOOLS_BUTTON_GAP,
                            flexShrink: 0,
                        }, children: [_jsx("button", { ref: emojiBtnRef, type: "button", onClick: onEmojiBtnClick, onMouseDown: (e) => e.preventDefault(), disabled: isSubmitting, title: "Insert emoji", style: {
                                    width: TOOLS_BUTTON_SIZE,
                                    height: TOOLS_BUTTON_SIZE,
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: showEmojiPicker ? 'var(--fern)' : DEW,
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }, children: _jsx(Smile, { size: 14 }) }), _jsx("button", { type: "button", onClick: () => insertAtCursor('@'), onMouseDown: (e) => e.preventDefault(), disabled: isSubmitting || collaborators.length === 0, title: "Mention someone", style: {
                                    width: TOOLS_BUTTON_SIZE,
                                    height: TOOLS_BUTTON_SIZE,
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: DEW,
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }, children: _jsx(AtSign, { size: 14 }) }), _jsx("button", { type: "button", onClick: onAttachClick, onMouseDown: (e) => e.preventDefault(), disabled: isSubmitting, title: "Attach file", style: {
                                    width: TOOLS_BUTTON_SIZE,
                                    height: TOOLS_BUTTON_SIZE,
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: DEW,
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }, children: _jsx(Paperclip, { size: 14 }) }), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: ".jpg,.jpeg,.png,.gif,.webp,.pdf", onChange: onFilesPicked, style: { display: 'none' } })] })] }), hasAttachments && (_jsx("div", { style: {
                    position: 'absolute',
                    left: 0,
                    top: BODY_H + 4,
                    width: BODY_W,
                    backgroundColor: SHELL,
                    borderRadius: 12,
                    boxShadow: PILL_SHADOW,
                    padding: 6,
                }, children: _jsx(AttachmentUploader, { pendingAttachments: pendingAttachments, onAttachmentsChange: setPendingAttachments, disabled: isSubmitting }) })), mentionQuery !== null && mentionAnchor && (_jsx(MentionPicker, { query: mentionQuery, collaborators: collaborators, anchor: mentionAnchor, onPick: handlePickMention, onClose: () => {
                    setMentionQuery(null);
                    mentionStartRef.current = null;
                } })), showEmojiPicker && emojiPickerPos && createPortal(_jsx("div", { ref: emojiPickerRef, style: {
                    position: 'fixed',
                    left: emojiPickerPos.x,
                    bottom: window.innerHeight - emojiPickerPos.y + 4,
                    width: 232,
                    backgroundColor: SHELL,
                    border: '1px solid var(--pewter)',
                    borderRadius: 8,
                    padding: 8,
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5)',
                    zIndex: 9999,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(8, 1fr)',
                    gap: 4,
                }, children: EMOJI_PALETTE.map((emoji) => (_jsx("button", { type: "button", onMouseDown: (e) => e.preventDefault(), onClick: () => pickEmoji(emoji), style: {
                        width: 24,
                        height: 24,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 16,
                        lineHeight: 1,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }, title: emoji, children: emoji }, emoji))) }), document.body)] }));
};
